import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { Position } from '@core/http-types';
import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';
import { JobBasic, OfferStatus } from '@core/offers-types';

import { CanAssignToJobsWithStatuses, EntityStoreKey, OfferScope } from '../../../types';

const { actions, namespace, selectors, reducer } = createEntityCollectionStore<JobBasic>()(
  EntityStoreKey.JobsBasic,
  {
    customActions: adapter => ({
      upsertOne: (state, payload: { data: JobBasic }) => adapter.upsertOne(state, payload.data),

      updatePositions: (state, payload: Position[]) =>
        pipeState(state, next =>
          adapter.markAsPending(next, { scope: OfferScope.Active, entities: [] }),
        ),
      updatePositionSuccess: (state, payload: Position[]) => {
        const updated = [...payload]
          .sort((a, b) => b.position - a.position)
          .map(({ id, position }) => ({
            ...state.entityMap[id]!,
            position,
          }));
        return pipeState(
          state,
          next => adapter.setMany(next, updated, { scope: OfferScope.Active }),
          next => adapter.markAsLoaded(next, { scope: OfferScope.Active, entities: [] }),
        );
      },
      updatePositionsFailed: (state, payload: { error: HttpErrorResponse }) =>
        pipeState(state, next =>
          adapter.setError(next, payload.error, { scope: OfferScope.Active, entities: [] }),
        ),

      switchJobsToProsemirror: ngrxEvent<void>(),
      switchJobsToProsemirrorSuccess: ngrxEvent(),
      switchJobsToProsemirrorFailed: ngrxEvent<{
        error: HttpErrorResponse;
      }>(),

      references: (state, payload: { data: JobBasic[] }) =>
        adapter.updateOrCreateMany(state, payload.data),
    }),
  },
);

// entities
const active = createSelector(selectors.allEntities, state =>
  state.filter(job => job.status !== OfferStatus.Archived).sort((a, b) => b.position - a.position),
);
const archived = createSelector(selectors.allEntities, state =>
  state.filter(job => job.status === OfferStatus.Archived).sort((a, b) => b.position - a.position),
);
const all = createSelector(active, archived, (active, archived) => [...active, ...archived]);
const activeByStatus = memoizeSelector((statuses: OfferStatus[]) =>
  createSelector(active, (state: JobBasic[]) => state.filter(job => statuses.includes(job.status))),
);
const activeByNotStatus = memoizeSelector((statuses: OfferStatus[]) =>
  createSelector(active, (state: JobBasic[]) =>
    state.filter(job => !statuses.includes(job.status)),
  ),
);
const activeBySlug = memoizeSelector((slug: string) =>
  createSelector(active, (state: JobBasic[]) => state.find(job => job.slug === slug)),
);
const bySlug = memoizeSelector((slug: string) =>
  createSelector(all, (state: JobBasic[]) => state.find(job => job.slug === slug)),
);
const canAssignTo = activeByStatus(CanAssignToJobsWithStatuses);

// meta
const activeScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Active }),
  state => state,
);

const customSelectors = {
  all,
  active,
  archived,
  activeByStatus,
  activeBySlug,
  canAssignTo,
  bySlug,
  activeByNotStatus,
  activeScopeIsLoaded,
};
const jobsSelectors = {
  ...selectors,
  ...customSelectors,
};

export {
  actions as JobsBasicActions,
  namespace as JobsBasicNamespace,
  jobsSelectors as JobsBasicSelectors,
  reducer as JobsBasicReducer,
};
